'use client';

import { useQuery } from '@tanstack/react-query';
import { queryClient } from '@/shared/lib/query-client';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { profileService } from '@/shared/api/services/profile.service';
import { BaseApiService } from '@/shared/api/services/base.service';
import type { FirstRunState } from '@/types/profile';
import type { JsonApiResponse, Project } from '@/types/project';

const HIDDEN: FirstRunState = {
  should_show: false,
  project_id: null,
  resume_step: null,
  angle_id: null,
};

/**
 * The server's first-run state. Seeded from every profile payload by
 * `applyProfileData`; the query function only runs when nothing seeded it
 * yet. A payload without the block (older API) reads as hidden.
 */
export function useFirstRunState(enabled: boolean): FirstRunState | undefined {
  const { data } = useQuery({
    queryKey: cacheKeys.firstRun,
    queryFn: async () => (await profileService.fetchProfile()).first_run ?? HIDDEN,
    enabled,
    staleTime: Infinity,
  });
  return data;
}

/** The wizard is over for this account: a render started or the user skipped. */
export function markFirstRunFinished(): void {
  queryClient.setQueryData<FirstRunState>(cacheKeys.firstRun, HIDDEN);
}

class FirstRunService extends BaseApiService {
  /**
   * The building the wizard uploads into: the oldest one, or a fresh default.
   * `first_run.project_id` is null when the account has no building yet.
   * Idempotent on the server.
   */
  async ensureProject(): Promise<string> {
    const response = await this.post<JsonApiResponse<Project>>('/v1/user/first-run/project');
    queryClient.invalidateQueries({ queryKey: cacheKeys.projects.all });
    return response.data.id;
  }

  /** "Skip": the server latches the wizard off for good. */
  async dismiss(): Promise<void> {
    await this.post<void>('/v1/user/first-run-dismissed');
  }
}

export const firstRunService = new FirstRunService();
